import dayjs from "dayjs";
import { find } from "lodash";
import { useEffect } from "react";

import {
  useGetV2PersonsByIdQuery,
  useLazyGetV2FilesDownloadUrlQuery,
  useLazyGetV2PersonsByPersonIdPhotosQuery,
  usePutV2PersonsByPersonIdPhotosStatusMutation,
} from "app/apiRTK";
import { getFioWithDots, normalizePhone } from "app/helpers";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { FileTypeEnum } from "app/types";

import { CheckIcon, XCloseIcon } from "assets/icons";

import AntButtonGreen from "components/AntButtonGreen";
import AntImage from "components/AntImage";
import createConfig from "components/AntMessageConfig";
import AntModal from "components/AntModal";
import AntSkeleton from "components/AntSkeleton";

import {
  isApproveFromUserPageState,
  openApprovePhotoModal,
  setIsNeedRefetchUserPage,
  setPersonIdThatNeedApprovePhoto,
} from "storeSlices/approvePhotoData";

import style from "./ApprovePhotoModal.module.scss";
import { IProps } from "./types";

const ApprovePhotoModal = (props: IProps) => {
  const { personId, messageApi, notificationsRefetch } = props;

  // useGet
  const { data: person } = useGetV2PersonsByIdQuery(personId);

  // useLazy
  const [getPersonPhotos] = useLazyGetV2PersonsByPersonIdPhotosQuery();
  const [downloadPhoto, { data: personPhotoUrl }] =
    useLazyGetV2FilesDownloadUrlQuery();

  // useMutation
  const [changePhotoStatus, { isLoading: isChangeStatusLoading }] =
    usePutV2PersonsByPersonIdPhotosStatusMutation();

  const dispatch = useAppDispatch();

  const isApproveFromUserPage = useAppSelector(isApproveFromUserPageState);

  useEffect(() => {
    getPersonPhotos(personId)
      .unwrap()
      .then((personPhotos) => {
        const needApprovePhoto = find(personPhotos, { status: 0 });

        if (needApprovePhoto) {
          downloadPhoto({
            fileType: FileTypeEnum["Фото профиля физ. лица"],
            key: needApprovePhoto.storageKey,
          });
        }
      });
  }, [getPersonPhotos, personId, downloadPhoto]);

  if (!personPhotoUrl || !personPhotoUrl.url || !person) {
    return <AntSkeleton />;
  }

  return (
    <AntModal
      title="Запрос на загрузку фото в профиль:"
      open={true}
      onCancel={() => {
        dispatch(openApprovePhotoModal(false));
      }}
      className={style.modal}
      footer={[
        <AntButtonGreen
          type="primary"
          key="cancel"
          onClick={() => {
            changePhotoStatus({ personId, status: 0 })
              .unwrap()
              .then(() => {
                notificationsRefetch()
                  .unwrap()
                  .then(() => {
                    dispatch(openApprovePhotoModal(false));
                    dispatch(setPersonIdThatNeedApprovePhoto(null));

                    // Проверяем если модалку вызвали со страницы редактирования пользователя
                    if (isApproveFromUserPage) {
                      dispatch(setIsNeedRefetchUserPage(true));
                    }

                    messageApi.open(
                      createConfig({
                        type: "success",
                        content: "Фото успешно отклонено!",
                      })
                    );
                  });
              })
              .catch(() => {
                messageApi.open(
                  createConfig({
                    type: "error",
                    content: "Возникла ошибка при отклонении фото",
                  })
                );
              });
          }}
          icon={<XCloseIcon color="red" />}
          loading={isChangeStatusLoading}
          ghost
          color="red"
        >
          Отклонить
        </AntButtonGreen>,

        <AntButtonGreen
          type="primary"
          key="ok"
          icon={<CheckIcon />}
          loading={isChangeStatusLoading}
          onClick={() => {
            changePhotoStatus({ personId, status: 1 })
              .unwrap()
              .then(() => {
                notificationsRefetch()
                  .unwrap()
                  .then(() => {
                    dispatch(openApprovePhotoModal(false));
                    dispatch(setPersonIdThatNeedApprovePhoto(null));

                    // Проверяем если модалку вызвали со страницы редактирования пользователя
                    if (isApproveFromUserPage) {
                      dispatch(setIsNeedRefetchUserPage(true));
                    }

                    messageApi.open(
                      createConfig({
                        type: "success",
                        content: "Фото успешно подтверждено!",
                      })
                    );
                  });
              })
              .catch(() => {
                messageApi.open(
                  createConfig({
                    type: "error",
                    content: "Возникла ошибка при подтверждении фото",
                  })
                );
              });
          }}
        >
          Подтвердить
        </AntButtonGreen>,
      ]}
    >
      <div className={style.modalContent}>
        <p className={style.text}>
          Подтвердите, что фотография пользователя соответствует его
          персональным данным. Убедитесь, что на изображении хорошо видно его
          лицо и отсутствуют посторонние лица. Это способствует эффективному и
          качественному прохождению медицинского осмотра пользователя.
        </p>

        <AntImage width={300} src={personPhotoUrl.url} />

        <p className={style.personInfo}>
          {getFioWithDots(
            person.lastName || "",
            person.firstName || "",
            person.patronymic || "",
            40
          )}
        </p>
        <p className={style.personInfo}>
          {dayjs(person.birthDate).format("D MMMM YYYY")} г.
        </p>
        <p className={style.personInfo}>{normalizePhone(person.phone)}</p>
      </div>
    </AntModal>
  );
};

export default ApprovePhotoModal;
